import { Plus } from 'lucide-react'
import { audioLine } from './fields.js'

const LINK_RE = /(https?:\/\/[^\s<>"')«]+|[\w.+-]+@[\w-]+(?:\.[\w-]+)+)/g

/** Links and email addresses inside the prose (Riverside rooms, upload
 *  pages, "the files go to abentham@whyy.org") stay tappable. */
export function Linkified({ text }) {
  return text.split(LINK_RE).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noreferrer" className="break-all text-link hover:underline">
        {part}
      </a>
    ) : /^[\w.+-]+@[\w-]+(\.[\w-]+)+$/.test(part) ? (
      <a key={i} href={`mailto:${part}`} className="break-all text-link hover:underline">
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  )
}

/**
 * The client's setup, boxed above the page (the reason this app was built
 * rather than bought). Only fields that exist render — blanks are omitted,
 * never shown as "—". Details is the note that closes the box ("prefers
 * U87…"). Everything in the box is changed only through the page's Edit
 * button (Charles, 2026-09-29); an empty record offers editors a prompt
 * instead of nothing. The client's free page sits under the Updates.
 */
export default function ClientCard({ client, canEdit, onEdit }) {
  const zoom =
    client.remoteMethods.includes('Zoom') &&
    (client.zoomLinkOwner === 'ours' ? 'Our link' : client.zoomLinkOwner === 'theirs' ? client.zoomLink || 'Their link' : null)

  const fields = [
    ['Sample rate', audioLine(client)],
    ['Remote', client.remoteMethods.join(', ')],
    ['Zoom link', zoom],
    ['Riverside link', client.remoteMethods.includes('Riverside') && client.riversideLink],
    ['Remote notes', client.remotePref],
    ['File delivery', client.fileDelivery],
    ['DAW', client.daw],
    ['Session type', client.sessionType],
  ].filter(([, v]) => v)

  if (fields.length === 0 && !client.details) {
    if (!canEdit) return null
    return (
      <button
        onClick={onEdit}
        className="tap mb-5 flex w-full items-center justify-center gap-1.5 rounded-[10px] border border-dashed border-hairline py-3 text-sm font-medium text-link active:bg-grouped"
      >
        <Plus size={15} /> Add sample rate, remote setup and details
      </button>
    )
  }

  return (
    <div className="card mb-5 p-4">
      {fields.length > 0 && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
          {fields.map(([label, value]) => (
            // long free text (links, notes) takes the full row so it can wrap
            <div key={label} className={'min-w-0 ' + (value.length > 40 ? 'col-span-full' : '')}>
              <div className="section-header">{label}</div>
              <div className="mt-0.5 whitespace-pre-line break-words text-sm font-medium text-ink">
                <Linkified text={value} />
              </div>
            </div>
          ))}
        </div>
      )}
      {client.details && (
        <div className={fields.length > 0 ? 'mt-3 border-t border-hairline pt-3' : ''}>
          <div className="section-header">Details</div>
          <div className="mt-0.5 whitespace-pre-line break-words text-sm text-ink">
            <Linkified text={client.details} />
          </div>
        </div>
      )}
    </div>
  )
}
